import React, { useCallback, useEffect, useState } from 'react'
import { QuantumThemeWrapper } from '@sendoutcards/quantum-design-ui'
import { performOperation } from 'src/app/api'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import {
  CreateSparseUser,
  CreateSparseUserData,
  InviteFragment,
  SponsorFragment,
} from 'src/graphql'
import { openApp } from 'src/helpers/appHelpers'
import useSelector from 'src/hooks/useSelector'
import { GreetingCardCarousel } from 'src/marketing/components/GreetingCardCarousel'
import { LaunchButton } from 'src/marketing/components/LaunchButton'
import {
  LaunchFeatureDialog,
  LaunchFeatureDialogKey,
} from 'src/marketing/components/LaunchFeatureDialog'
import { LaunchSection } from 'src/marketing/components/LaunchSection'
import { Menu, MenuItem } from 'src/marketing/components/Menu'
import {
  LaunchAction,
  launchCatalogCards,
  LaunchSection as LaunchSectionType,
  launchSections,
} from './launchPageContent'
import styles from './launchPage.module.scss'

type LaunchPageType = { invite?: InviteFragment }

type CookiePreferenceWindow = Window & {
  Cookiebot?: { renew?: () => void }
  OneTrust?: { ToggleInfoDisplay?: () => void }
}

const getSponsorSuffix = (sponsor: SponsorFragment | undefined): string => {
  if (!sponsor) return ''
  return sponsor.genealogyId
    ? `${sponsor.genealogyId}`
    : sponsor.storefront && sponsor.storefront.slug
    ? `${sponsor.storefront.slug}`
    : ''
}

const LaunchPage: React.FC<LaunchPageType> = ({ invite }) => {
  const storefrontUser = useSelector(state => state.storefront.storefront?.user)
  const [activeDialog, setActiveDialog] = useState<
    LaunchFeatureDialogKey | undefined
  >()
  const [isDarkSectionActive, setIsDarkSectionActive] = useState(true)

  useEffect(() => {
    const hideCookieLaunchers = () => {
      document
        .querySelectorAll<HTMLElement>(
          '#CookiebotWidget, #ot-sdk-btn-floating, .cky-btn-revisit, .cc-revoke, [aria-label*="cookie" i][role="button"], button[aria-label*="cookie" i]',
        )
        .forEach(element => {
          element.style.setProperty('display', 'none', 'important')
        })
    }

    hideCookieLaunchers()

    const observer = new MutationObserver(hideCookieLaunchers)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const visibleDarkSections = new Set<string>()
    const darkSectionIds = Object.values(launchSections)
      .filter(section => section.theme === 'dark_galaxy')
      .map(section => section.sectionKey)
    const darkSectionElements = darkSectionIds
      .map(id => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    if (darkSectionElements.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleDarkSections.add(entry.target.id)
          } else {
            visibleDarkSections.delete(entry.target.id)
          }
        })

        setIsDarkSectionActive(visibleDarkSections.size > 0)
      },
      {
        rootMargin: '-72px 0px -65% 0px',
        threshold: 0,
      },
    )

    darkSectionElements.forEach(element => observer.observe(element))

    return () => observer.disconnect()
  }, [])

  const goToApp = useCallback((path = '', query?: Record<string, string>) => {
    openApp(path, query)
  }, [])

  const scrollToSection = useCallback((targetId: string) => {
    document
      .getElementById(targetId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const openPromptings = useCallback(() => {
    window.location.href = `https://www.promptings.com/?sponsor=${getSponsorSuffix(
      storefrontUser,
    )}`
  }, [storefrontUser])

  const openPricing = useCallback(() => {
    window.location.href = `https://app.sendoutcards.com/pricing${
      storefrontUser ? '?sponsor=' + getSponsorSuffix(storefrontUser) : ''
    }`
  }, [storefrontUser])

  const handleSendFreeCard = useCallback(() => {
    if (invite) {
      performOperation<CreateSparseUserData>(
        CreateSparseUser({ inviteId: invite.id }),
        result => {
          if (result.value) {
            goToApp('', {
              access_token: result.value.createSparseUser.account.token,
              postcardPromo: '',
            })
            return
          }

          goToApp('', {
            verify: '',
            postcardPromo: '',
          })
        },
      )
      return
    }

    goToApp('', {
      verify: '',
      postcardPromo: '',
    })
  }, [goToApp, invite])

  const resolveAction = useCallback(
    (action?: LaunchAction) => {
      if (!action) return undefined

      return () => {
        switch (action.type) {
          case 'send_free_card':
            handleSendFreeCard()
            return
          case 'pricing':
            goToApp('pricing')
            return
          case 'scroll_to': {
            scrollToSection(action.targetId)
            return
          }
          case 'external_url':
            if (action.url) {
              window.location.href = action.url
            }
            return
          default:
            return
        }
      }
    },
    [goToApp, handleSendFreeCard, scrollToSection],
  )

  const handleOpenDialog = useCallback((modalKey: LaunchFeatureDialogKey) => {
    setActiveDialog(modalKey)
  }, [])

  const openCookiePreferences = useCallback(() => {
    const cookieWindow = window as CookiePreferenceWindow

    if (cookieWindow.Cookiebot?.renew) {
      cookieWindow.Cookiebot.renew()
      return
    }

    if (cookieWindow.OneTrust?.ToggleInfoDisplay) {
      cookieWindow.OneTrust.ToggleInfoDisplay()
      return
    }

    const launcher = document.querySelector<HTMLElement>(
      '#CookiebotWidget, #ot-sdk-btn-floating, .ot-sdk-show-settings, .cky-btn-revisit, .cc-revoke, [aria-label*="cookie" i]',
    )

    launcher?.click()
  }, [])

  const menuItems: MenuItem[] = [
    {
      label: 'Send a Free Card',
      detail: 'Start with a free card',
      icon: 'card',
      onClick: handleSendFreeCard,
    },
    {
      label: 'Promptings',
      detail: 'Open promptings.com',
      icon: 'promptings',
      onClick: openPromptings,
    },
    {
      label: 'Pricing',
      detail: 'View plans and pricing',
      icon: 'pricing',
      onClick: openPricing,
    },
    {
      label: 'My Account',
      detail: 'Open your account',
      icon: 'login',
      onClick: goToApp,
      separatorBefore: true,
    },
    {
      label: 'Site Preferences',
      detail: 'Manage cookie settings',
      icon: 'cookie',
      onClick: openCookiePreferences,
    },
  ]

  const renderSection = (section: LaunchSectionType) => (
    <LaunchSection
      key={section.id}
      section={section}
      onPrimaryAction={resolveAction(section.primaryAction)}
      onSecondaryAction={resolveAction(section.secondaryAction)}
      onScrollPoint={scrollToSection}
      onInfoPoint={handleOpenDialog}
    />
  )

  return (
    <QuantumThemeWrapper>
      <main className={styles.page}>
        <header className={styles.header}>
          <div className={`${styles.shell} ${styles.headerRow}`}>
            <button
              className={styles.brand}
              type="button"
              onClick={() => scrollToSection('hero')}
              aria-label="Promptings home"
            >
              <Icon name="promptings_powered_by_soc" />
            </button>

            <nav
              className={styles.navLinks}
              aria-label="Launch page navigation"
            >
              <button type="button" onClick={openPromptings}>
                Promptings
              </button>
              <button type="button" onClick={openPricing}>
                Pricing
              </button>
              <button type="button" onClick={() => goToApp()}>
                My Account
              </button>
            </nav>

            <div className={styles.headerActions}>
              <LaunchButton
                label="Send a Free Card"
                variant="primary"
                className={styles.headerCta}
                onClick={handleSendFreeCard}
              />
            </div>

            <div className={styles.mobileMenu}>
              <Menu
                items={menuItems}
                variant={isDarkSectionActive ? 'dark' : 'light'}
              />
            </div>
          </div>
        </header>
        <div className={styles.content}>
          {renderSection(launchSections.hero)}

          <section
            id="cards-ribbon"
            className={styles.ribbonSection}
            aria-labelledby="cards-ribbon-heading"
          >
            <div className={styles.shell}>
              <div className={styles.ribbonIntro}>
                <p className={styles.eyebrow}>Our Catalog</p>
                <h2 id="cards-ribbon-heading">
                  Thousands of world-class designs, ready for the moment you
                  feel it.
                </h2>
                <p>
                  Browse an industry-leading catalog with thousands of
                  world-class designs crafted by our professional designers for
                  birthdays, gratitude, business follow-up, encouragement,
                  celebrations, and everything in between.
                </p>
              </div>
              <GreetingCardCarousel
                cards={launchCatalogCards}
                cardWidth={132}
                cardHeight={185}
                gap={16}
              />
            </div>
          </section>

          {renderSection(launchSections.actOnPromptings)}
          {renderSection(launchSections.digitalAndPrinted)}
          {renderSection(launchSections.crm)}
          {renderSection(launchSections.customization)}
          {renderSection(launchSections.gifts)}
        </div>
        <div className={styles.backToTopSection}>
          <button
            className={styles.backToTopButton}
            type="button"
            onClick={() => scrollToSection('hero')}
            aria-label="Back to top"
          >
            <span>Back to top</span>
            <span className={styles.backToTopChevron} aria-hidden="true" />
          </button>
        </div>
        <LaunchFeatureDialog
          activeDialog={activeDialog}
          onClose={() => setActiveDialog(undefined)}
        />
      </main>
    </QuantumThemeWrapper>
  )
}

export default LaunchPage
